import { Outlet, useLocation } from 'react-router-dom';
import { useMsalAuthState, DISABLED_AUTH_STATE, type AuthState, isAuthDisabled, hrdEnabled } from '@/auth';
import { HrdLogin } from '@platform-ui/auth/react';
import { defaultAuthority, domainMappings } from '@/auth/auth-config';
import { env } from '@/config/env';
import { Spinner } from '@aero/ui';

const HRD_STORAGE_PREFIX = 'portal-auth';

/**
 * Loading screen shown while auth is initializing.
 */
function AuthLoadingScreen() {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                height: '100%',
                backgroundColor: 'rgba(255,255,255,0.4)'
            }}>
            <Spinner className="size-4" />
            <p style={{ color: '#64748b', fontSize: '1rem' }}>Authenticating...</p>
        </div>
    );
}

/**
 * Auth layout content when auth is enabled.
 * Uses MSAL hooks to check authentication state.
 */
function AuthenticatedLayout() {
    const authState = useMsalAuthState();
    const location = useLocation();
    return (
        <AuthLayoutContent
            authState={authState}
            currentPath={location.pathname}
        />
    );
}

/**
 * Auth layout content - handles auth state and renders children or shows HRD login.
 */
function AuthLayoutContent({ authState, currentPath }: { authState: AuthState; currentPath: string }) {
    // Show loading screen while auth is initializing
    if (authState.isLoading) {
        return <AuthLoadingScreen />;
    }

    // Show HRD login inline if not authenticated — URL stays the same so
    // the user returns to their current page after the B2C redirect.
    if (!isAuthDisabled && hrdEnabled && !authState.isAuthenticated) {
        return (
            <HrdLogin
                currentPath={currentPath}
                defaultAuthority={defaultAuthority}
                domainMappings={domainMappings}
                storageKeyPrefix={HRD_STORAGE_PREFIX}
                hrdHeaderMenu={env.auth.hrdHeaderMenuItems}
                hrdFooterMenu={env.auth.hrdFooterMenuItems}
            />
        );
    }

    // User is authenticated (or auth is disabled) - render children
    return <Outlet />;
}

/**
 * Auth layout - protects child routes by requiring authentication.
 *
 * Behavior:
 * - When auth is disabled: renders children immediately
 * - When auth is loading: shows loading screen
 * - When not authenticated (HRD enabled): shows HRD login inline
 * - When authenticated: renders children via Outlet
 *
 * Uses component-level branching to avoid conditional hook calls.
 */
export function AuthLayout() {
    // When auth is disabled, skip MSAL hooks entirely
    if (isAuthDisabled) {
        return (
            <AuthLayoutContent
                authState={DISABLED_AUTH_STATE}
                currentPath="/"
            />
        );
    }

    return <AuthenticatedLayout />;
}
